import { fetchWithAuth } from '@/lib/fetchWithAuth';
import type {
  ClienteFiscal,
  ConfigFiscal,
  ContaReceberOpcao,
  NotaFiscal,
  NotaFiscalResumo,
  ProdutoRevenda,
  RegraFiscal,
  StatusIntegracaoFiscal,
} from '@/types/notasFiscais';

const BASE = '/api/notas-fiscais';

export class NotaFiscalApiError extends Error {
  detalhes: string[];

  constructor(message: string, detalhes: string[] = []) {
    super(message);
    this.name = 'NotaFiscalApiError';
    this.detalhes = detalhes;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetchWithAuth(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
    timeoutMs: 90_000,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new NotaFiscalApiError(data?.error || `Erro ${res.status}`, Array.isArray(data?.detalhes) ? data.detalhes : []);
  }
  return data as T;
}

const json = (body: unknown) => JSON.stringify(body);

export const notasFiscaisApi = {
  status: () => request<StatusIntegracaoFiscal>('/status'),

  obterConfig: () => request<ConfigFiscal>('/config'),
  salvarConfig: (campos: Partial<ConfigFiscal>) => request<ConfigFiscal>('/config', { method: 'PUT', body: json(campos) }),

  listarRegras: () => request<RegraFiscal[]>('/regras'),
  criarRegra: (regra: Partial<RegraFiscal>) => request<RegraFiscal>('/regras', { method: 'POST', body: json(regra) }),
  salvarRegra: (id: string, regra: Partial<RegraFiscal>) =>
    request<RegraFiscal>(`/regras/${id}`, { method: 'PUT', body: json(regra) }),

  listarProdutos: (params: { busca?: string; ativos?: boolean } = {}) => {
    const qs = new URLSearchParams();
    if (params.busca) qs.set('busca', params.busca);
    if (params.ativos) qs.set('ativos', '1');
    const sufixo = qs.toString() ? `?${qs}` : '';
    return request<ProdutoRevenda[]>(`/produtos${sufixo}`);
  },
  criarProduto: (produto: Partial<ProdutoRevenda>) =>
    request<ProdutoRevenda>('/produtos', { method: 'POST', body: json(produto) }),
  salvarProduto: (id: string, produto: Partial<ProdutoRevenda>) =>
    request<ProdutoRevenda>(`/produtos/${id}`, { method: 'PUT', body: json(produto) }),
  excluirProduto: (id: string) => request<{ ok: true }>(`/produtos/${id}`, { method: 'DELETE' }),

  buscarClientes: (busca: string) =>
    request<ClienteFiscal[]>(`/clientes?busca=${encodeURIComponent(busca)}`),
  salvarDadosFiscaisCliente: (leadId: string, campos: Record<string, unknown>) =>
    request<{ id: string }>(`/clientes/${leadId}/fiscal`, { method: 'PUT', body: json(campos) }),

  buscarContasReceber: (busca: string) =>
    request<ContaReceberOpcao[]>(`/contas-receber?busca=${encodeURIComponent(busca)}`),

  listarNotas: () => request<NotaFiscalResumo[]>('/notas'),
  obterNota: (id: string) => request<NotaFiscal>(`/notas/${id}`),
  criarNota: (nota: Partial<NotaFiscal>) => request<NotaFiscal>('/notas', { method: 'POST', body: json(nota) }),
  salvarNota: (id: string, nota: Partial<NotaFiscal>) =>
    request<NotaFiscal>(`/notas/${id}`, { method: 'PUT', body: json(nota) }),
  excluirNota: (id: string) => request<{ ok: true }>(`/notas/${id}`, { method: 'DELETE' }),
  emitirNota: (id: string) => request<NotaFiscal>(`/notas/${id}/emitir`, { method: 'POST' }),
  sincronizarNota: (id: string) => request<NotaFiscal>(`/notas/${id}/sincronizar`, { method: 'POST' }),
  cancelarNota: (id: string, justificativa: string) =>
    request<NotaFiscal>(`/notas/${id}/cancelar`, { method: 'POST', body: json({ justificativa }) }),
  cartaCorrecao: (id: string, correcao: string) =>
    request<NotaFiscal>(`/notas/${id}/carta-correcao`, { method: 'POST', body: json({ correcao }) }),
  enviarEmail: (id: string, emails: string[]) =>
    request<{ enviados: string[] }>(`/notas/${id}/email`, { method: 'POST', body: json({ emails }) }),

  inutilizar: (dados: { serie: number; numero_inicial: number; numero_final: number; justificativa: string }) =>
    request<Record<string, unknown>>('/inutilizar', { method: 'POST', body: json(dados) }),

  listarWebhook: () => request<{ ambiente: string; hooks: Array<{ id: string; url: string }> }>('/webhook'),
  registrarWebhook: () =>
    request<{ ok: true; ambiente: string; hook: { id: string; url: string } }>('/webhook/registrar', { method: 'POST' }),
};

export const formatarMoeda = (valor: number | null | undefined) =>
  (Number(valor) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const formatarQuantidade = (valor: number | string | null | undefined, unidade?: string | null) => {
  const n = Number(valor);
  const texto = (Number.isFinite(n) ? n : 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
  return unidade ? `${texto} ${unidade}` : texto;
};

export const formatarDocumento = (doc?: string | null) => {
  const d = String(doc || '').replace(/\D/g, '');
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return doc || '';
};

export const mensagemErro = (error: unknown) => {
  if (error instanceof NotaFiscalApiError && error.detalhes.length) {
    return `${error.message}: ${error.detalhes.join(' ')}`;
  }
  return error instanceof Error ? error.message : 'Erro inesperado';
};
